// Service for managing notifications using Firebase Firestore
import {
  addNotification as firestoreAddNotification,
  getNotifications as firestoreGetNotifications,
  updateNotification as firestoreUpdateNotification,
  deleteNotification as firestoreDeleteNotification,
  Notification
} from '../firebase/firestore';
import { onSnapshot, collection, query, orderBy, where } from 'firebase/firestore';
import { db } from '../firebase/firebase-config';

// Re-export the Notification interface from firestore
export type { Notification };

// Add a new notification
export const addNotification = async (notification: Omit<Notification, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  return await firestoreAddNotification(notification);
};

// Get all notifications
export const getNotifications = async (userId?: string): Promise<Notification[]> => {
  return await firestoreGetNotifications(userId);
};

// Listen to notifications changes in real-time
export const subscribeToNotifications = (callback: (notifications: Notification[]) => void, userId?: string): (() => void) => {
  let q = query(collection(db, "notifications"));
  
  if (userId) {
    q = query(q, where("targetUserId", "==", userId));
  }
  
  q = query(q, orderBy("createdAt", "desc"));
  
  return onSnapshot(q, (snapshot) => {
    const notifications = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Notification));
    callback(notifications);
  });
};

// Mark notification as read
export const markNotificationAsRead = async (id: string): Promise<void> => {
  return await firestoreUpdateNotification(id, { read: true });
};

// Mark all notifications as read
export const markAllNotificationsAsRead = async (userId?: string): Promise<void> => {
  const notifications = await firestoreGetNotifications(userId);
  const unreadNotifications = notifications.filter(n => !n.read);
  
  await Promise.all(
    unreadNotifications.map(notification => 
      firestoreUpdateNotification(notification.id!, { read: true })
    )
  );
};

// Delete a notification
export const deleteNotification = async (id: string): Promise<void> => {
  return await firestoreDeleteNotification(id);
};

// Utility function to create notifications for various events
export const createTaskOverdueNotification = async (taskId: string, taskTitle: string, assignedTo: string) => {
  return addNotification({
    title: 'Task Overdue',
    message: `Task "${taskTitle}" is overdue and requires attention.`,
    type: 'task_overdue',
    read: false,
    targetUserId: assignedTo,
    relatedId: taskId
  });
};

export const createAppointmentTodayNotification = async (appointmentId: string, candidateName: string, time: string) => {
  return addNotification({
    title: 'Appointment Today',
    message: `Appointment with ${candidateName} is scheduled for ${time} today.`,
    type: 'appointment_today',
    read: false,
    relatedId: appointmentId
  });
};

export const createCandidateStageChangeNotification = async (candidateId: string, candidateName: string, oldStage: string, newStage: string) => {
  return addNotification({
    title: 'Candidate Stage Updated',
    message: `${candidateName} has moved from ${oldStage} to ${newStage}.`,
    type: 'candidate_stage_change',
    read: false,
    relatedId: candidateId
  });
};

export const createRoleExpirationNotification = async (userId: string, daysRemaining: number) => {
  return addNotification({
    title: 'Role Expiration Warning',
    message: `Your role expires in ${daysRemaining} day(s). Please contact administrator.`,
    type: 'role_expiration',
    read: false,
    targetUserId: userId
  });
};